/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useState, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { SanitaryVisitRecord } from '@/types/models';
import { loadSanitaryVisits, saveSanitaryVisits } from '@/context/utils/localStorage';

export interface SanitaryVisitContextState {
  sanitaryVisitRecords: SanitaryVisitRecord[];
  addSanitaryVisitRecord: (record: Omit<SanitaryVisitRecord, 'id'>) => void;
  updateSanitaryVisitRecord: (id: string, updates: Partial<SanitaryVisitRecord>) => void;
  deleteSanitaryVisitRecord: (id: string) => void;
}

const SanitaryVisitContext = createContext<SanitaryVisitContextState | undefined>(undefined);

export const SanitaryVisitProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sanitaryVisitRecords, setSanitaryVisitRecords] = useState<SanitaryVisitRecord[]>(
    loadSanitaryVisits()
  );

  const addSanitaryVisitRecord = useCallback((record: Omit<SanitaryVisitRecord, 'id'>) => {
    const newRecord = { ...record, id: uuidv4() };
    setSanitaryVisitRecords(current => {
      const updated = [...current, newRecord];
      saveSanitaryVisits(updated);
      return updated;
    });
  }, []);

  const updateSanitaryVisitRecord = useCallback((id: string, updates: Partial<SanitaryVisitRecord>) => {
    setSanitaryVisitRecords(current => {
      const updated = current.map(record => 
        record.id === id ? { ...record, ...updates } : record
      );
      saveSanitaryVisits(updated);
      return updated;
    });
  }, []);

  const deleteSanitaryVisitRecord = useCallback((id: string) => {
    setSanitaryVisitRecords(current => {
      const updated = current.filter(record => record.id !== id);
      saveSanitaryVisits(updated);
      return updated;
    });
  }, []);

  const value = useMemo(() => ({
    sanitaryVisitRecords,
    addSanitaryVisitRecord,
    updateSanitaryVisitRecord,
    deleteSanitaryVisitRecord,
  }), [sanitaryVisitRecords, addSanitaryVisitRecord, updateSanitaryVisitRecord, deleteSanitaryVisitRecord]);

  return (
    <SanitaryVisitContext.Provider value={value}>
      {children}
    </SanitaryVisitContext.Provider>
  );
};

export const useSanitaryVisitContext = () => {
  const context = React.useContext(SanitaryVisitContext);
  if (!context) {
    throw new Error('useSanitaryVisitContext must be used within SanitaryVisitProvider');
  }
  return context;
};
